const DEFAULT_MIN_DEPOSIT_PERCENT = 10;
const DEFAULT_MAX_DEPOSIT_PERCENT = 50;

function getMinDepositPercent(container) {
  const min = parseFloat(container?.dataset.minDepositPercent);
  return Number.isNaN(min) ? DEFAULT_MIN_DEPOSIT_PERCENT : min;
}

function getMaxDepositPercent(container) {
  const max = parseFloat(container?.dataset.maxDepositPercent);
  return Number.isNaN(max) ? DEFAULT_MAX_DEPOSIT_PERCENT : max;
}

function clampDepositPercent(container, value) {
  const min = getMinDepositPercent(container);
  const max = getMaxDepositPercent(container);
  const parsed = parseFloat(value);
  if (Number.isNaN(parsed)) return min;
  return Math.min(max, Math.max(min, parsed));
}

function getDepositPercent(container, depositInput, { enforce = false } = {}) {
  const raw = depositInput?.value;
  const clamped = clampDepositPercent(container, raw);

  if (enforce && depositInput && raw !== '' && String(raw) !== String(clamped)) {
    depositInput.value = String(clamped);
  }

  return clamped;
}

function calculateDeposit(cashPrice, depositPercent) {
  const deposit = parseInt(Math.ceil(cashPrice * depositPercent), 10) / 100;
  return parseFloat(deposit.toFixed(2));
}

function formatMoney(value) {
  const num = parseFloat(value);
  if (Number.isNaN(num)) return value;
  return num.toFixed(2);
}

function buildResultsHtml(payments) {
  if (!payments || !payments.productAvailable) {
    return '<p>Finance is not available for this price and deposit combination.</p>';
  }

  const parts = [];
  parts.push(`<p><strong>Total cash price:</strong> £${formatMoney(payments.cashPrice)}</p>`);

  if (parseFloat(payments.deposit) > 0) {
    parts.push(`<p><strong>Deposit:</strong> £${formatMoney(payments.deposit)}</p>`);
  }

  parts.push(`<p><strong>Amount of credit:</strong> £${formatMoney(payments.loanAmount)}</p>`);

  let repayableText = '';
  if (payments.monthsDeferred > 0) {
    repayableText = `Non-payment period ${payments.monthsDeferred} months, followed by `;
  } else {
    repayableText = 'Repayable by ';
  }

  if (payments.initialPayments !== payments.finalPayment && parseFloat(payments.finalPayment) > 0) {
    repayableText += `${payments.months - 1} monthly repayments of £${formatMoney(payments.initialPayments)} and a final payment of £${formatMoney(payments.finalPayment)}.`;
  } else {
    repayableText += `${payments.months} monthly repayments of £${formatMoney(payments.initialPayments)}.`;
  }

  parts.push(`<p>${repayableText}</p>`);
  parts.push(`<p><strong>Representative APR:</strong> ${payments.apr}%</p>`);
  parts.push(`<p><strong>Annual rate of interest:</strong> ${payments.annualRate}% fixed</p>`);

  if (parseFloat(payments.documentFee) > 0) {
    parts.push(`<p><strong>Arrangement fee:</strong> £${formatMoney(payments.documentFee)}</p>`);
  }

  if (parseFloat(payments.settlementFee) > 0) {
    parts.push(`<p><strong>Settlement fee:</strong> £${formatMoney(payments.settlementFee)}</p>`);
  }

  parts.push(
    `<p><strong>Total amount repayable:</strong> £${formatMoney(payments.amountPayable)} including total interest of £${formatMoney(payments.interest)}.</p>`
  );

  return parts.join('');
}

function populateProductSelect(select) {
  if (!select || select.dataset.v12ProductsReady === 'true') return;
  if (typeof window.V12 === 'undefined' || typeof window.V12.getFinanceProducts !== 'function') return;

  select.innerHTML = '';
  window.V12.getFinanceProducts().forEach((product) => {
    const option = document.createElement('option');
    option.value = product.productId;
    option.textContent = product.name;
    select.appendChild(option);
  });

  select.dataset.v12ProductsReady = 'true';
}

function calculateContainer(container) {
  if (!container || typeof window.V12 === 'undefined') return;

  const cashInput = container.querySelector('[data-v12-calculator-cash-price]');
  const depositInput = container.querySelector('[data-v12-calculator-deposit]');
  const productSelect = container.querySelector('[data-v12-calculator-product]');
  const resultsEl = container.querySelector('[data-v12-calculator-results]');

  if (!cashInput || !depositInput || !productSelect || !resultsEl) return;

  populateProductSelect(productSelect);

  const cashPrice = parseFloat(cashInput.value);
  const depositPercent = getDepositPercent(container, depositInput);
  const productId = productSelect.value;

  if (Number.isNaN(cashPrice) || Number.isNaN(depositPercent) || !productId) {
    resultsEl.innerHTML = '<p>Enter a valid cash price and deposit to see finance options.</p>';
    return;
  }

  const financeProduct = window.V12.getFinanceProduct(productId);
  if (!financeProduct) {
    resultsEl.innerHTML = '<p>Unable to load finance product details.</p>';
    return;
  }

  const deposit = calculateDeposit(cashPrice, depositPercent);
  const payments = window.V12.calculate(financeProduct, cashPrice, deposit);
  resultsEl.innerHTML = buildResultsHtml(payments);
}

function selectLowestMonthlyProduct(container) {
  if (!container || typeof window.V12 === 'undefined') return;

  const cashInput = container.querySelector('[data-v12-calculator-cash-price]');
  const depositInput = container.querySelector('[data-v12-calculator-deposit]');
  const productSelect = container.querySelector('[data-v12-calculator-product]');

  if (!cashInput || !depositInput || !productSelect) return;

  populateProductSelect(productSelect);

  const cashPrice = parseFloat(cashInput.value);
  const depositPercent = getDepositPercent(container, depositInput);
  if (Number.isNaN(cashPrice)) return;

  const deposit = calculateDeposit(cashPrice, depositPercent);
  let lowestMonthly = null;
  let lowestProductId = null;

  window.V12.getFinanceProducts().forEach((fp) => {
    const financeProduct = window.V12.getFinanceProduct(fp.productId);
    if (!financeProduct) return;

    const payments = window.V12.calculate(financeProduct, cashPrice, deposit);
    if (!payments.productAvailable) return;

    const monthly = parseFloat(payments.initialPayments);
    if (lowestMonthly === null || monthly < lowestMonthly) {
      lowestMonthly = monthly;
      lowestProductId = fp.productId;
    }
  });

  if (lowestProductId) {
    productSelect.value = String(lowestProductId);
  }

  calculateContainer(container);
}

function bindCalculator(container) {
  if (!container || container.dataset.v12CalculatorBound === 'true') return;

  const cashInput = container.querySelector('[data-v12-calculator-cash-price]');
  const depositInput = container.querySelector('[data-v12-calculator-deposit]');
  const productSelect = container.querySelector('[data-v12-calculator-product]');
  const lowestBtn = container.querySelector('[data-v12-calculator-lowest]');

  const recalculate = () => calculateContainer(container);

  cashInput?.addEventListener('input', recalculate);
  depositInput?.addEventListener('input', recalculate);
  depositInput?.addEventListener('change', () => {
    getDepositPercent(container, depositInput, { enforce: true });
    recalculate();
  });
  depositInput?.addEventListener('blur', () => {
    getDepositPercent(container, depositInput, { enforce: true });
    recalculate();
  });
  productSelect?.addEventListener('change', recalculate);
  lowestBtn?.addEventListener('click', () => selectLowestMonthlyProduct(container));

  if (depositInput && depositInput.value !== '') {
    getDepositPercent(container, depositInput, { enforce: true });
  }

  container.dataset.v12CalculatorBound = 'true';
  recalculate();
}

function initV12Calculators(root = document) {
  root.querySelectorAll('[data-v12-calculator]').forEach((container) => {
    bindCalculator(container);
  });
}

function syncVariantPrice(variant) {
  if (!variant?.price) return;

  const cashPrice = variant.price / 100;

  document.querySelectorAll('[data-v12-finance-message]').forEach((el) => {
    el.dataset.price = cashPrice;
    delete el.dataset.v12FinanceReady;
    el.setAttribute('hidden', '');
    el.setAttribute('aria-hidden', 'true');
    el.textContent = '';
  });

  document.querySelectorAll('[data-v12-calculator]').forEach((container) => {
    container.dataset.cashPrice = cashPrice;
    const cashInput = container.querySelector('[data-v12-calculator-cash-price]');
    if (cashInput) cashInput.value = cashPrice.toFixed(2);
    calculateContainer(container);
  });

  window.V12Finance?.init?.();
}

function bootCalculators() {
  initV12Calculators();
}

bootCalculators();

let calcAttempts = 0;
const calcRetry = window.setInterval(() => {
  calcAttempts += 1;
  const pending = document.querySelectorAll('[data-v12-calculator]:not([data-v12-calculator-bound])');
  if (pending.length === 0 || calcAttempts >= 40) {
    window.clearInterval(calcRetry);
    return;
  }
  initV12Calculators();
}, 250);

document.addEventListener('shopify:section:load', bootCalculators);
window.addEventListener('pageshow', bootCalculators);

window.ThemeBus?.addEventListener('variant:change', (event) => {
  syncVariantPrice(event.detail?.variant);
});

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-modal-toggle^="v12-finance-calculator-"]');
  if (!trigger) return;

  const modalId = trigger.getAttribute('data-modal-toggle');
  window.setTimeout(() => {
    const modal = document.querySelector(`modal-component[data-modal-id="${modalId}"]`);
    initV12Calculators(modal || document);
  }, 50);
});

window.V12FinanceCalculator = {
  init: initV12Calculators,
  calculate: calculateContainer,
};
